import { type RequestHandlerOptions } from "msw";

import { getGetInsightsLimitedPartnerViewMockHandler } from "@vega-api/generated/insights-lp-view/insights-lp-view.msw";
import { type ApiInsightsLpViewResponse } from "@vega-api/generated/types/apiInsightsLpViewResponse";
import { BarChartMetricAllOfBarChartDataOrientation } from "@vega-api/generated/types/barChartMetricAllOfBarChartDataOrientation";
import { BarChartMetricAllOfBarChartDataValueFormat } from "@vega-api/generated/types/barChartMetricAllOfBarChartDataValueFormat";
import { Currency } from "@vega-api/generated/types/currency";
import { InsightsDealActivityStatusEnum } from "@vega-api/generated/types/insightsDealActivityStatusEnum";
import { type InsightTrackingMetric } from "@vega-api/generated/types/insightTrackingMetric";
import { type LimitedPartnerDealActivity } from "@vega-api/generated/types/limitedPartnerDealActivity";
import { type LimitedPartnerFundBreakdown } from "@vega-api/generated/types/limitedPartnerFundBreakdown";
import { type LimitedPartnerFundBreakdownTotals } from "@vega-api/generated/types/limitedPartnerFundBreakdownTotals";
import { type LimitedPartnerInsightsLpView } from "@vega-api/generated/types/limitedPartnerInsightsLpView";

export const lpInsightsMockResponse: ApiInsightsLpViewResponse = {
  data: {
    kpis: [
      {
        label: "Co-investments",
        value: "3",
        type: "COUNT",
        description: "Co-investments you have committed to",
        countMetricDiscriminator: "TRUE",
      },
      {
        label: "Co-investments Total",
        value: "$51.0M",
        type: "CURRENCY",
        description: "Total capital you have committed across co-investments",
        currencyMetricDiscriminator: "TRUE",
        moneyValue: { amount: 51000000, currency: Currency.USD },
      },
      {
        label: "Avg. Ticket Size",
        value: "$17.0M",
        type: "CURRENCY",
        description: "Average committed amount per co-investment",
        currencyMetricDiscriminator: "TRUE",
        moneyValue: { amount: 17000000, currency: Currency.USD },
      },
      {
        label: "Deals Shared",
        value: "6",
        type: "COUNT",
        description: "Deals shared with you",
        countMetricDiscriminator: "TRUE",
      },
      {
        label: "Avg. Gross MOIC",
        value: "1.02x",
        type: "TEXT",
        description:
          "Average gross multiple on invested capital across your committed deals",
        textMetricDiscriminator: "TRUE",
      },
    ],
    charts: [
      {
        label: "Commitments by Year",
        value: "$51.0M",
        type: "BAR_CHART",
        description: "Your committed capital per calendar year",
        barChartMetricDiscriminator: "TRUE",
        barChartData: {
          orientation: BarChartMetricAllOfBarChartDataOrientation.VERTICAL,
          valueFormat: BarChartMetricAllOfBarChartDataValueFormat.CURRENCY,
          currency: Currency.USD,
          bars: [{ label: "2026", value: 51000000 }],
        },
      },
      {
        label: "Deals by Status",
        value: "6",
        type: "PIE_CHART",
        description: "Breakdown of your shared deals by current status",
        pieChartMetricDiscriminator: "TRUE",
        pieChartData: {
          totalCount: 6,
          slices: [
            { label: "Committed", value: 3, percentage: 50 },
            { label: "Actively Prospecting", value: 2, percentage: 33.3 },
            { label: "Passed", value: 1, percentage: 16.7 },
          ],
        },
      },
      {
        label: "Sector",
        value: "3",
        type: "PIE_CHART",
        description: "Breakdown of your committed co-investments by sector",
        pieChartMetricDiscriminator: "TRUE",
        pieChartData: {
          totalCount: 3,
          slices: [
            { label: "Financial Services", value: 1, percentage: 33.3 },
            { label: "Healthcare", value: 1, percentage: 33.3 },
            { label: "Technology", value: 1, percentage: 33.4 },
          ],
        },
      },
    ],
    deals: [],
  },
};

const { kpis: fullKpis, charts: fullCharts } = lpInsightsMockResponse.data;

const extraKpis: InsightTrackingMetric[] = [
  {
    label: "Deals Committed",
    value: "4",
    type: "COUNT",
    description: "Deals you have committed to",
    countMetricDiscriminator: "TRUE",
  },
  {
    label: "Realised Value",
    value: "$12.0M",
    type: "CURRENCY",
    description: "Capital returned across your co-investments",
    currencyMetricDiscriminator: "TRUE",
    moneyValue: { amount: 12000000, currency: Currency.USD },
  },
];

const buildLpInsightsResponse = (
  data: Partial<LimitedPartnerInsightsLpView> &
    Pick<LimitedPartnerInsightsLpView, "charts" | "kpis">,
): ApiInsightsLpViewResponse => ({ data: { deals: [], ...data } });

export const lpInsightsVisibilityFixtures = {
  manyKpis: buildLpInsightsResponse({
    kpis: [...fullKpis, ...extraKpis],
    charts: fullCharts,
  }),
  singleKpiSingleChart: buildLpInsightsResponse({
    kpis: fullKpis.slice(0, 1),
    charts: fullCharts.slice(0, 1),
  }),
  kpisOnly: buildLpInsightsResponse({
    kpis: fullKpis.slice(0, 3),
    charts: [],
  }),
  chartsOnly: buildLpInsightsResponse({
    kpis: [],
    charts: fullCharts.slice(0, 2),
  }),
  empty: buildLpInsightsResponse({ kpis: [], charts: [] }),
} as const;

const committedDeal: LimitedPartnerDealActivity = {
  dealId: "deal-committed-1",
  dealOpportunityId: "opp-committed-1",
  dealName: "Project Atlas",
  status: InsightsDealActivityStatusEnum.COMMITTED,
  year: 2026,
  committedAmount: { amount: 17000000, currency: Currency.USD },
  moicGross: 1.4,
};

const committedDealWithoutReporting: LimitedPartnerDealActivity = {
  dealId: "deal-committed-2",
  dealOpportunityId: "opp-committed-2",
  dealName: "Project Borealis",
  status: InsightsDealActivityStatusEnum.COMMITTED,
  year: 2025,
  committedAmount: { amount: 12000000, currency: Currency.USD },
};

const prospectingDeal: LimitedPartnerDealActivity = {
  dealId: "deal-prospecting-1",
  dealOpportunityId: "opp-prospecting-1",
  dealName: "Project Cascade",
  status: InsightsDealActivityStatusEnum.PROSPECTING,
  year: 2026,
};

const passedDeal: LimitedPartnerDealActivity = {
  dealId: "deal-passed-1",
  dealOpportunityId: "opp-passed-1",
  dealName: "Project Delta",
  status: InsightsDealActivityStatusEnum.PASSED,
  year: 2024,
};

const committedDealWithoutOpportunity: LimitedPartnerDealActivity = {
  dealId: "deal-committed-3",
  dealName: "Project Everest",
  status: InsightsDealActivityStatusEnum.COMMITTED,
  year: 2026,
  committedAmount: { amount: 9000000, currency: Currency.USD },
};

const fundBreakdown: LimitedPartnerFundBreakdown[] = [
  {
    fundId: "fund-1",
    fundName: "Vega Growth Fund II",
    primaryCommitment: { amount: 50000000, currency: Currency.USD },
    coinvestments: 2,
    coinvestmentTotal: { amount: 29000000, currency: Currency.USD },
    coinvestToPrimaryRatio: 0.58,
    dealsShared: 4,
  },
];

const fundBreakdownTotals: LimitedPartnerFundBreakdownTotals = {
  primaryCommitment: { amount: 50000000, currency: Currency.USD },
  coinvestments: 2,
  coinvestmentTotal: { amount: 29000000, currency: Currency.USD },
  coinvestToPrimaryRatio: 0.58,
  dealsShared: 4,
};

const baseMetrics = { kpis: fullKpis, charts: fullCharts };

export const lpInsightsDealFixtures = {
  fullSection: buildLpInsightsResponse({
    ...baseMetrics,
    deals: [committedDeal, committedDealWithoutReporting, prospectingDeal],
    passedDeals: [passedDeal],
    fundBreakdown,
    fundBreakdownTotals,
  }),
  passedTabDisabled: buildLpInsightsResponse({
    ...baseMetrics,
    deals: [committedDeal, prospectingDeal],
    fundBreakdown,
    fundBreakdownTotals,
  }),
  fundBreakdownDisabled: buildLpInsightsResponse({
    ...baseMetrics,
    deals: [committedDeal],
    passedDeals: [],
  }),
  metricsHidden: buildLpInsightsResponse({
    kpis: [],
    charts: [],
    deals: [committedDeal],
  }),
  missingOpportunityId: buildLpInsightsResponse({
    ...baseMetrics,
    deals: [committedDealWithoutOpportunity],
  }),
} satisfies Record<string, ApiInsightsLpViewResponse>;

export const getLpInsightsMockHandler = (
  overrideResponse: Parameters<
    typeof getGetInsightsLimitedPartnerViewMockHandler
  >[0] = lpInsightsMockResponse,
  options?: RequestHandlerOptions,
) => getGetInsightsLimitedPartnerViewMockHandler(overrideResponse, options);
